import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ButtonsContainer, Button, Modal, Input, nanoid, mock } from '@forma/forma-ui-kit';
import TableColumnsSelect from './TableColumnsSelect';
import TableRowsSelect from './TableRowsSelect';
import { defaultProductsColumns, defaultProductsTotal } from 'data/mock';

import { ITemplateSide, ITemplateTable, ITemplateTableColumn, ITemplateTableTotalRow } from 'interfaces/templates.interface';
import { IProductPropertiesItem } from 'interfaces/products.interface';
import { TVariableType } from 'interfaces/variables.interface';

import styles from './products-table-select-modal.module.css';

interface ProductsTableSelectModalProps {
  open: boolean,
  onClose: () => void,
  onSelect: (sideId: string, data: ITemplateTable) => void,
  data?: ITemplateTable | null,
  sides: ITemplateSide[],
  sideId?: string,
  properties?: IProductPropertiesItem[],
  onCreateColumn?: (data: Omit<IProductPropertiesItem, 'id'>) => Promise<string | null>
  onEditColumn?: (data: IProductPropertiesItem) => Promise<string | null>,
  onRemoveColumn?: (id: string) => void,
}

const defColumns = defaultProductsColumns.map(id => mock.productTableColumns.find(item => item.id === id)!);
const defTotal = defaultProductsTotal.map(id => mock.productTableFooter.find(item => item.id === id)!);

const ProductsTableSelectModal: FC<ProductsTableSelectModalProps> = ({
  open, onClose, onSelect, data, sides, sideId, properties, onCreateColumn, onEditColumn, onRemoveColumn
}) => {
  const { t } = useTranslation();
  const defaultTableName = t('new_table');

  const [name, setName] = useState<string>(defaultTableName);
  const [columns, setColumns] = useState<ITemplateTableColumn[]>(defColumns);
  const [totalRows, setTotalRows] = useState<ITemplateTableTotalRow[]>(defTotal);
  const [showTotalRow, setShowTotalRow] = useState<boolean>(true);
  const [side, setSide] = useState<ITemplateSide | null>(null);

  useEffect(() => {
    setColumns(prev => {
      const next = [...prev];
      properties?.forEach(({ id, name, type }) => {
        const index = next.findIndex(col => col.id === id);
        if (index !== -1) next[index] = { ...next[index], name, type };
      });
      return next;
    });
  }, [properties]);

  useEffect(() => {
    if (data) {
      setName(data.name);
      setColumns(data.columns);
      setTotalRows(data.total);
      setShowTotalRow(data.showTotalRow ?? false);
    }
  }, [data]);

  useEffect(() => {
    if (sides && sideId) setSide(sides.find(({ id }) => sideId === id) ?? null);
    // eslint-disable-next-line
  }, [sides, sideId]);

  const handleClose = () => {
    onClose();
    setName(defaultTableName);
    setColumns(defColumns);
    setTotalRows(defTotal);
    setShowTotalRow(true);
  };

  const handleSelectCol = (column: { id: string, name: string, type?: TVariableType, width?: string }) => {
    setColumns(prev => [...prev, column]);
  };

  const handleDeselectCol = (id: string) => {
    setColumns(prev => prev.filter(column => column.id !== id));
  };

  const handleSelectTotal = (row: { id: string, name: string }) => {
    setTotalRows(prev => [...prev, row]);
  };

  const handleDeselectTotal = (id: string) => {
    setTotalRows(prev => prev.filter(row => row.id !== id));
  };

  const handleSubmit = () => {
    if (!side) return;

    onSelect(side.id, { id: data?.id ?? nanoid(), name, columns, total: totalRows, showTotalRow });
    setSide(null);
  };

  const handleAddCol = ({ name, type }: Omit<IProductPropertiesItem, 'id'>) => {
    onCreateColumn!({ name, type }).then(id => {
      if (!id) return;
      setColumns(prev => [...prev, { id, name, width: '50px' }]);
    });
  };

  if (!side) return (
    <Modal
      size="small"
      open={open}
      title={<span>{t('add_table_products_services')}:<br />{t('select_side')}</span>}
      onClose={onClose}
    >
      <ButtonsContainer className={styles.buttons}>
        {sides.map(side => (
          <Button
            // id={`select_table_button_${id}`} // id for onboarding
            viewStyle="tertiary"
            onClick={() => setSide(side)}
            fullWidth
            key={side.id}
          >
            {side.name}
          </Button>
        ))}
      </ButtonsContainer>
    </Modal>
  );

  return (
    <Modal
      width="100%"
      maxWidth="1100px"
      open={open}
      onClose={handleClose}
      title={data ? t('edit_table_products_services') : t('add_table_products_services')}
      buttonsClassName={styles.modalButtons}
      buttons={[{
        className: styles.modalButton,
        viewStyle: 'primary',
        onClick: handleSubmit,
        disabled: !columns.length || name.length < 3,
        children: data ? t('uikit:save') : t('uikit:add')
      }]}
    >
      <input type="hidden" autoFocus />
      <Input
        name="table_name"
        viewStyle="secondary"
        className={styles.name}
        placeholder={t('enter_table_name')}
        onChange={e => setName(e.target.value)}
        value={name}
      />
      <div className={styles.content}>
        <TableColumnsSelect
          columns={columns}
          properties={properties}
          onSelect={handleSelectCol}
          onDeselect={handleDeselectCol}
          onCreateColumn={handleAddCol}
          onRemoveColumn={onRemoveColumn}
          onEditColumn={onEditColumn}
        />
        <TableRowsSelect
          total={totalRows}
          onSelect={handleSelectTotal}
          onDeselect={handleDeselectTotal}
          showTotalRow={showTotalRow}
          onSetTotalRow={setShowTotalRow}
        />
      </div>
    </Modal>
  );
};

export default ProductsTableSelectModal;
